import React, { createContext, useContext, ReactNode } from 'react';
import ApiClient from '../../lib/api-client';
import { ISysSettingCreationDTO } from '@common-frontend-submodule/interfaces/sys-setting/isys-setting-creation.interface';
import { ISysSettingUpdateDTO } from '@common-frontend-submodule/interfaces/sys-setting/isys-setting-update.interface';
import { IGetSysSettingsRequestDTO } from '@common-frontend-submodule/interfaces/sys-setting/iget-sys-settings-request.interface';
import { IPaginatedResultDTO } from '@common-frontend-submodule/interfaces/common/ipaginated-result.interface';
import { IApiResponseGenericDTO } from '@common-frontend-submodule/interfaces/common/iapi-response.interface';
import { SysSettingRoutes } from '@common-frontend-submodule/enums/routes/sys-setting-routes.enum';
import { ISysSettingDTO } from '@common-frontend-submodule/interfaces/sys-setting/isys-setting.interface';

const apiClient = ApiClient.getInstance()

interface SysSettingContextProps {
  getById: (id: string) => Promise<IApiResponseGenericDTO<ISysSettingDTO>>;
  getAll: () => Promise<IApiResponseGenericDTO<ISysSettingDTO[]>>;
  filter: (model: IGetSysSettingsRequestDTO) => Promise<IApiResponseGenericDTO<IPaginatedResultDTO<ISysSettingDTO>>>;
  create: (model: ISysSettingCreationDTO) => Promise<IApiResponseGenericDTO<ISysSettingDTO>>;
  update: (id: string, model: ISysSettingUpdateDTO) => Promise<IApiResponseGenericDTO<ISysSettingDTO>>;
  delete: (id: string) => Promise<IApiResponseGenericDTO<boolean>>;
}

const SysSettingContext = createContext<SysSettingContextProps | undefined>(undefined);

export const SysSettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const getById = async (id: string): Promise<IApiResponseGenericDTO<ISysSettingDTO>> => {
    return await apiClient.getRequest<ISysSettingDTO>(`${SysSettingRoutes.BasePrefix}/${id}`);
  };

  const getAll = async (): Promise<IApiResponseGenericDTO<ISysSettingDTO[]>> => {
    return await apiClient.getRequest<ISysSettingDTO[]>(`${SysSettingRoutes.BasePrefix}/${SysSettingRoutes.All}`);
  };

  const filter = async (model: IGetSysSettingsRequestDTO): Promise<IApiResponseGenericDTO<IPaginatedResultDTO<ISysSettingDTO>>> => {
    return await apiClient.postRequest<IGetSysSettingsRequestDTO, IPaginatedResultDTO<ISysSettingDTO>>(
      `${SysSettingRoutes.BasePrefix}/${SysSettingRoutes.List}`,
      model
    );
  };

  const create = async (model: ISysSettingCreationDTO): Promise<IApiResponseGenericDTO<ISysSettingDTO>> => {
    return await apiClient.postRequest<ISysSettingCreationDTO, ISysSettingDTO>(SysSettingRoutes.BasePrefix, model);
  };

  const update = async (id: string, model: ISysSettingUpdateDTO): Promise<IApiResponseGenericDTO<ISysSettingDTO>> => {
    return await apiClient.putRequest<ISysSettingUpdateDTO, ISysSettingDTO>(`${SysSettingRoutes.BasePrefix}/${id}`, model);
  };

  const deleteSysSetting = async (id: string): Promise<IApiResponseGenericDTO<boolean>> => {
    return await apiClient.deleteRequest<null, boolean>(`${SysSettingRoutes.BasePrefix}/${id}`);
  };

  return (
    <SysSettingContext.Provider value={{ getById, getAll, filter, create, update, delete: deleteSysSetting }}>
      {children}
    </SysSettingContext.Provider>
  );
};

export const useSysSettingStore = () => {
  const context = useContext(SysSettingContext);
  if (!context) {
    throw new Error('useSysSettingStore must be used within a SysSettingProvider');
  }
  return context;
};
